import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Typography, Button, IconButton, Tooltip, CircularProgress, Paper,
  TextField, Grid, FormControl, InputLabel, Select, MenuItem, Autocomplete, Chip,
  FormControlLabel, Checkbox, Alert,
} from '@mui/material';
import {
  Add as AddIcon, Delete as DeleteIcon, Refresh as RefreshIcon, Inventory as InventoryIcon,
  VerifiedUser as TraceIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { getApiUrl } from '../../config/environment';
import { TRACEABILITY_CHECKS, type TraceabilityChecks } from '../../pages/Inventory/inventoryConstants';
import { EVOLUTION_QUICK_ADD_ROUTES, type AdministrationRoute } from '../../constants/inventoryConsumption';

export type EvolutionConsumptionLine = {
  item_id: string;
  batch_id?: string | null;
  quantity: number;
  reservation_id?: string | null;
  procedure_id?: string | null;
  laterality?: string | null;
  notes?: string | null;
  source?: string;
  item_name?: string | null;
  unit?: string | null;
  requires_traceability?: boolean;
  lot_number?: string | null;
  individual_code?: string | null;
  traceability_checks?: TraceabilityChecks;
};

const LATERALITY_OPTIONS = [
  { value: '', label: '—' },
  { value: 'od', label: 'OD' },
  { value: 'oe', label: 'OE' },
  { value: 'ao', label: 'AO' },
];

export type CompanionConsumptionHint = {
  parent_item_id: string;
  companion_item_id: string;
  companion_name: string;
  quantity: number;
  consumption_scope: 'per_movement' | 'per_evolution';
  unit?: string | null;
};

type Props = {
  patientId?: string;
  procedureIds: string[];
  procedureSessions?: Array<{ procedure_id: string; session_number?: number | null }>;
  disabled?: boolean;
  initialLines?: EvolutionConsumptionLine[];
  alreadyApplied?: boolean;
  value: EvolutionConsumptionLine[];
  onChange: (lines: EvolutionConsumptionLine[]) => void;
};

const defaultChecks = (): TraceabilityChecks => ({
  patient_identity: false,
  medical_record: false,
  billing_account: false,
  lot_label: false,
});

const EvolutionInventoryConsumptionSection: React.FC<Props> = ({
  patientId,
  procedureIds,
  procedureSessions,
  disabled,
  initialLines,
  alreadyApplied,
  value,
  onChange,
}) => {
  const [loading, setLoading] = useState(false);
  const [itemOptions, setItemOptions] = useState<any[]>([]);
  const [itemSearch, setItemSearch] = useState('');
  const [reqMap, setReqMap] = useState<Record<string, boolean>>({});
  const [companionHints, setCompanionHints] = useState<CompanionConsumptionHint[]>([]);
  const [suggestWarnings, setSuggestWarnings] = useState<string[]>([]);
  const [quickItemsByRoute, setQuickItemsByRoute] = useState<Record<string, any[]>>({});

  const traceableItemIds = useMemo(
    () => value.filter((l) => l.item_id && (l.requires_traceability || reqMap[l.item_id])).map((l) => l.item_id),
    [value, reqMap],
  );

  const loadRequirements = useCallback(async (itemIds: string[], lines = value) => {
    const ids = Array.from(new Set(itemIds.filter(Boolean)));
    if (!ids.length) return;
    try {
      const res = await axios.get(getApiUrl('/inventory-module/traceability/requirements'), {
        params: { item_ids: ids.join(',') },
      });
      const next: Record<string, boolean> = {};
      for (const row of res.data.data || []) {
        next[row.item_id] = !!row.requires_traceability;
      }
      setReqMap((prev) => ({ ...prev, ...next }));
      const needsSync = lines.some((l) => l.item_id && next[l.item_id] && !l.requires_traceability);
      if (needsSync) {
        onChange(lines.map((l) => {
          if (!l.item_id || !next[l.item_id]) return l;
          return {
            ...l,
            requires_traceability: true,
            traceability_checks: l.traceability_checks || defaultChecks(),
          };
        }));
      }
    } catch {
      /* módulo pode ainda não existir */
    }
  }, [value, onChange]);

  const parseSuggestPayload = (raw: unknown): {
    lines: EvolutionConsumptionLine[];
    hints: CompanionConsumptionHint[];
    warnings: string[];
  } => {
    if (Array.isArray(raw)) {
      return { lines: raw as EvolutionConsumptionLine[], hints: [], warnings: [] };
    }
    const obj = (raw || {}) as Record<string, unknown>;
    return {
      lines: (obj.lines as EvolutionConsumptionLine[]) || [],
      hints: (obj.companion_hints as CompanionConsumptionHint[]) || [],
      warnings: (obj.warnings as string[]) || [],
    };
  };

  const loadSuggest = useCallback(async () => {
    if (!patientId || alreadyApplied) return;
    setLoading(true);
    try {
      const sessions = procedureSessions?.filter((p) => p.procedure_id) || [];
      const res = await axios.get(getApiUrl('/inventory-module/evolution-consumption/suggest'), {
        params: sessions.length > 0
          ? {
            patient_id: patientId,
            procedure_sessions: JSON.stringify(sessions),
          }
          : {
            patient_id: patientId,
            procedure_ids: procedureIds.filter(Boolean).join(',') || undefined,
          },
      });
      const { lines: suggestedRaw, hints, warnings } = parseSuggestPayload(res.data.data);
      const suggested: EvolutionConsumptionLine[] = suggestedRaw.map((l: EvolutionConsumptionLine) => ({
        ...l,
        traceability_checks: l.requires_traceability ? defaultChecks() : undefined,
      }));
      setCompanionHints(hints);
      setSuggestWarnings(warnings);
      if (suggested.length > 0 && value.length === 0) {
        onChange(suggested);
        loadRequirements(suggested.map((l) => l.item_id));
      }
    } catch {
      /* tabela/módulo pode ainda não existir */
    } finally {
      setLoading(false);
    }
  }, [patientId, procedureIds, procedureSessions, alreadyApplied, value.length, onChange, loadRequirements]);

  useEffect(() => {
    if (disabled || alreadyApplied) return;
    const routes = EVOLUTION_QUICK_ADD_ROUTES.map((r) => r.route).join(',');
    axios.get(getApiUrl('/inventory-module/consumption-quick-items'), { params: { routes } })
      .then((res) => {
        const items: any[] = res.data.data || [];
        const grouped: Record<string, any[]> = {};
        for (const item of items) {
          const route = item.metadata?.administration_route as string;
          if (!route) continue;
          if (!grouped[route]) grouped[route] = [];
          grouped[route].push(item);
        }
        setQuickItemsByRoute(grouped);
      })
      .catch(() => setQuickItemsByRoute({}));
  }, [disabled, alreadyApplied]);

  const addQuickItem = (route: AdministrationRoute) => {
    const options = quickItemsByRoute[route] || [];
    if (!options.length) {
      addManualLine();
      return;
    }
    const item = options[0];
    const exists = value.some((l) => l.item_id === item.id);
    if (exists && (route === 'kit_iv_soro' || route === 'kit_iv_seringa')) return;
    onChange([...value, {
      item_id: item.id,
      quantity: 1,
      source: 'quick_add',
      item_name: item.name,
      unit: item.unit,
      requires_traceability: !!item.requires_traceability,
      traceability_checks: item.requires_traceability ? defaultChecks() : undefined,
    }]);
    if (item.id) loadRequirements([item.id]);
  };

  useEffect(() => {
    if (initialLines?.length && value.length === 0) {
      onChange(initialLines);
      loadRequirements(initialLines.map((l) => l.item_id));
      return;
    }
    if (patientId && !alreadyApplied && value.length === 0) {
      loadSuggest();
    }
  }, [
    patientId,
    procedureIds.join(','),
    JSON.stringify(procedureSessions || []),
    alreadyApplied,
    initialLines,
    value.length,
    onChange,
    loadRequirements,
    loadSuggest,
  ]);

  useEffect(() => {
    const missing = value.map((l) => l.item_id).filter((id) => id && reqMap[id] === undefined);
    if (missing.length) loadRequirements(missing);
  }, [value, reqMap, loadRequirements]);

  useEffect(() => {
    const t = setTimeout(() => {
      if (!itemSearch) return;
      axios.get(getApiUrl('/inventory-module/items'), { params: { search: itemSearch, limit: 15 } })
        .then((res) => setItemOptions(res.data.data || []))
        .catch(() => setItemOptions([]));
    }, 300);
    return () => clearTimeout(t);
  }, [itemSearch]);

  const updateLine = (idx: number, patch: Partial<EvolutionConsumptionLine>) => {
    onChange(value.map((l, i) => (i === idx ? { ...l, ...patch } : l)));
  };

  const updateChecks = (idx: number, key: keyof TraceabilityChecks, checked: boolean) => {
    const line = value[idx];
    updateLine(idx, {
      traceability_checks: { ...(line.traceability_checks || defaultChecks()), [key]: checked },
    });
  };

  const removeLine = (idx: number) => onChange(value.filter((_, i) => i !== idx));

  const addManualLine = () => {
    onChange([...value, { item_id: '', quantity: 1, source: 'manual' }]);
  };

  const lineNeedsTraceability = (line: EvolutionConsumptionLine) =>
    !!(line.requires_traceability || (line.item_id && reqMap[line.item_id]));

  const traceabilityIncomplete = traceableItemIds.some((itemId) => {
    const line = value.find((l) => l.item_id === itemId);
    if (!line) return false;
    const checks = line.traceability_checks || defaultChecks();
    if (!checks.patient_identity || !checks.medical_record || !checks.billing_account) return true;
    if ((line.batch_id || line.lot_number) && !checks.lot_label) return true;
    return false;
  });

  if (!patientId) return null;

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2, borderColor: '#cbd5e1' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <InventoryIcon sx={{ color: '#0D9488', fontSize: 20 }} />
          <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: '#0f172a' }}>
            Consumo de estoque
          </Typography>
          {alreadyApplied && (
            <Chip label="Baixado" size="small" sx={{ bgcolor: '#d1fae5', color: '#065f46', fontWeight: 600 }} />
          )}
        </Box>
        {!disabled && !alreadyApplied && (
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <Tooltip title="Recarregar sugestões (reservas + materiais do procedimento)">
              <IconButton size="small" onClick={loadSuggest} disabled={loading}>
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Button size="small" startIcon={<AddIcon />} onClick={addManualLine} sx={{ textTransform: 'none' }}>
              Material
            </Button>
          </Box>
        )}
      </Box>

      <Typography variant="body2" sx={{ color: '#64748b', mb: 1.5 }}>
        Kits fixos (SC/IM) vêm do procedimento; medicações variáveis e soro EV você adiciona aqui.
        Um soro pode atender várias medicações — use &quot;+ Soro EV&quot; uma vez por atendimento.
        Materiais rastreáveis exigem checklist antes da baixa.
      </Typography>

      {!disabled && !alreadyApplied && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1.5 }}>
          {EVOLUTION_QUICK_ADD_ROUTES.map((q) => (
            <Button
              key={q.route}
              size="small"
              variant="outlined"
              onClick={() => addQuickItem(q.route)}
              sx={{ textTransform: 'none', borderColor: '#cbd5e1', color: '#0f172a' }}
            >
              {q.label}
            </Button>
          ))}
        </Box>
      )}

      {suggestWarnings.length > 0 && (
        <Alert severity="info" sx={{ mb: 1.5, borderRadius: 1.5 }}>
          {suggestWarnings.join(' ')}
        </Alert>
      )}

      {companionHints.length > 0 && !alreadyApplied && (
        <Alert severity="success" sx={{ mb: 1.5, borderRadius: 1.5 }}>
          Baixa automática de companheiros ao concluir:&nbsp;
          {companionHints.map((h) => (
            <Chip
              key={`${h.parent_item_id}-${h.companion_item_id}`}
              size="small"
              label={`${h.companion_name} ×${h.quantity}${h.consumption_scope === 'per_evolution' ? ' (1/evolução)' : ''}`}
              sx={{ mr: 0.5, mb: 0.5 }}
            />
          ))}
        </Alert>
      )}

      {!alreadyApplied && traceableItemIds.length > 0 && traceabilityIncomplete && (
        <Alert severity="warning" sx={{ mb: 1.5, borderRadius: 1.5 }}>
          Confirme o checklist de rastreabilidade para cada material implantável antes de concluir.
        </Alert>
      )}

      {loading ? (
        <Box sx={{ py: 2, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress size={22} sx={{ color: '#0D9488' }} />
        </Box>
      ) : value.length === 0 ? (
        <Typography variant="body2" sx={{ color: '#94a3b8', fontStyle: 'italic' }}>
          {alreadyApplied ? 'Nenhum material registrado nesta evolução.' : 'Nenhum material sugerido. Adicione manualmente se necessário.'}
        </Typography>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
          {value.map((line, idx) => {
            const needsTrace = lineNeedsTraceability(line);
            const checks = line.traceability_checks || defaultChecks();
            const hasBatch = !!(line.batch_id || line.lot_number);
            return (
              <Box key={`${line.item_id}-${idx}`} sx={{
                p: 1.25, borderRadius: 1.5, bgcolor: '#f8fafc', border: '1px solid #e2e8f0',
              }}>
                <Grid container spacing={1} alignItems="center">
                  <Grid item xs={12} sm={5}>
                    {alreadyApplied || disabled ? (
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {line.item_name || line.item_id}
                        {line.source === 'reservation' && (
                          <Chip label="Reserva" size="small" sx={{ ml: 1, height: 18, fontSize: 10 }} />
                        )}
                        {needsTrace && (
                          <Chip icon={<TraceIcon sx={{ fontSize: 12 }} />} label="Rastreável" size="small"
                            sx={{ ml: 0.5, height: 18, fontSize: 10, bgcolor: '#fef3c7', color: '#92400e' }} />
                        )}
                      </Typography>
                    ) : (
                      <Autocomplete
                        size="small"
                        options={itemOptions}
                        getOptionLabel={(o) => o.name || ''}
                        value={itemOptions.find((o) => o.id === line.item_id) || (line.item_name ? { id: line.item_id, name: line.item_name } : null)}
                        onChange={(_, v) => {
                          updateLine(idx, {
                            item_id: v?.id || '',
                            item_name: v?.name || null,
                            unit: v?.unit || null,
                            requires_traceability: !!v?.requires_traceability,
                            traceability_checks: v?.requires_traceability ? defaultChecks() : undefined,
                          });
                          if (v?.id) loadRequirements([v.id]);
                        }}
                        onInputChange={(_, v) => setItemSearch(v)}
                        renderInput={(params) => <TextField {...params} label="Produto" />}
                      />
                    )}
                  </Grid>
                  <Grid item xs={4} sm={2}>
                    <TextField
                      fullWidth size="small" type="number" label="Qtd"
                      value={line.quantity}
                      disabled={disabled || alreadyApplied}
                      onChange={(e) => updateLine(idx, { quantity: Number(e.target.value) || 0 })}
                      inputProps={{ min: 0.001, step: 0.001 }}
                    />
                  </Grid>
                  <Grid item xs={4} sm={2}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Lateralidade</InputLabel>
                      <Select
                        value={line.laterality || ''}
                        label="Lateralidade"
                        disabled={disabled || alreadyApplied}
                        onChange={(e) => updateLine(idx, { laterality: e.target.value || null })}
                      >
                        {LATERALITY_OPTIONS.map((o) => (
                          <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={4} sm={2}>
                    <Typography variant="caption" color="text.secondary">
                      {line.unit || 'un'}
                      {line.lot_number ? ` · ${line.lot_number}` : line.batch_id ? ' · lote' : ''}
                      {line.individual_code ? ` · ${line.individual_code}` : ''}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={1} sx={{ textAlign: 'right' }}>
                    {!disabled && !alreadyApplied && (
                      <IconButton size="small" onClick={() => removeLine(idx)}>
                        <DeleteIcon sx={{ fontSize: 16, color: '#ef4444' }} />
                      </IconButton>
                    )}
                  </Grid>
                </Grid>

                {needsTrace && (
                  <Box sx={{ mt: 1.25, pt: 1, borderTop: '1px dashed #e2e8f0' }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#92400e', display: 'block', mb: 0.5 }}>
                      Checklist de rastreabilidade
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {TRACEABILITY_CHECKS.filter((c) => c.always || (c.whenBatch && hasBatch)).map((c) => (
                        <FormControlLabel
                          key={c.key}
                          control={(
                            <Checkbox
                              size="small"
                              checked={!!checks[c.key]}
                              disabled={disabled || alreadyApplied}
                              onChange={(e) => updateChecks(idx, c.key, e.target.checked)}
                            />
                          )}
                          label={<Typography variant="caption">{c.label}</Typography>}
                          sx={{ mr: 1 }}
                        />
                      ))}
                    </Box>
                  </Box>
                )}

                {alreadyApplied && line.traceability_checks && (
                  <Box sx={{ mt: 1 }}>
                    {TRACEABILITY_CHECKS.filter((c) => line.traceability_checks?.[c.key]).map((c) => (
                      <Chip key={c.key} label={c.label} size="small" sx={{ mr: 0.5, mb: 0.5, height: 20, fontSize: 10 }} />
                    ))}
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
      )}
    </Paper>
  );
};

export default EvolutionInventoryConsumptionSection;
